import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Settings, Languages } from "lucide-react";
import { useLanguagePreference } from "@/hooks/useLanguagePreference";
import { AdvancedTrackingDialog } from "./AdvancedTrackingDialog";
import { analyticsEvents } from "@/services/analytics";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { PaymentModeCard } from "./PaymentModeCard";
import { BusinessHoursSettings } from "./BusinessHoursSettings";
import { useMenuStore } from "@/store/menuStore";

export function PreferenceSettings() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const {
    preference,
    isLoading: languageLoading,
    updateLanguagePreference,
  } = useLanguagePreference();
  const { stores, fetchStores, toggleInventoryTracking, selectedStoreId } =
    useStoreManagementStore((state) => state);
  const { advancedInventoryTracking } = useFeatureFlagStore();
  const { clearMenuItems, resetStore } = useMenuStore();
  const [isLoading, setIsLoading] = useState(false);
  const [advancedMenuTracking, setAdvancedMenuTracking] =
    useState<boolean>(false);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [pendingTrackingState, setPendingTrackingState] = useState<
    boolean | null
  >(null);

  // Track page view
  useEffect(() => {
    analyticsEvents.trackPageView("settings_preferences", {
      user_id: user?.id,
    });
  }, [user]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    if (selectedStoreId && stores) {
      const selectedStore = stores.find(
        (store) => store.id === selectedStoreId
      );
      setAdvancedMenuTracking(
        selectedStore?.advanced_inventory_tracking || false
      );
    }
  }, [selectedStoreId, stores]);

  const handleLanguageChange = async (newLanguage: string) => {
    await updateLanguagePreference(newLanguage);
    analyticsEvents.settings.preferenceChanged("language", newLanguage);
  };

  const handleToggleRequest = (checked: boolean) => {
    // If feature flag doesn't allow advanced tracking and user is trying to enable it
    if (!advancedInventoryTracking && checked) {
      toast.error(t("featureNotAvailable"));
      return;
    }

    setPendingTrackingState(checked);
    setShowTrackingDialog(true);

    // Track toggle click
    analyticsEvents.trackEvent("advanced_inventory_toggle_clicked", {
      requested_state: checked,
      store_id: selectedStoreId,
    });
  };

  const handleToggleConfirm = async () => {
    if (pendingTrackingState === null || !selectedStoreId) return;

    setIsLoading(true);
    try {
      const success = await toggleInventoryTracking(
        selectedStoreId,
        pendingTrackingState
      );

      if (success) {
        setAdvancedMenuTracking(pendingTrackingState);

        // Track successful inventory mode toggle
        analyticsEvents.inventory.modeToggled(
          selectedStoreId,
          pendingTrackingState
        );

        toast.success(
          pendingTrackingState
            ? t("advancedMenuTrackingEnabled")
            : t("advancedMenuTrackingDisabled")
        );
      } else {
        toast.error(t("errorUpdatingPreference"));
      }
    } catch (error) {
      console.error("Error toggling inventory tracking:", error);
      toast.error(t("errorUpdatingPreference"));
    } finally {
      setIsLoading(false);
      setShowTrackingDialog(false);
      setPendingTrackingState(null);
    }
  };

  const selectedStore =
    selectedStoreId && stores
      ? stores.find((store) => store.id === selectedStoreId)
      : null;

  return (
    <div className="space-y-6">
      {/* Language settings card */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Languages className="h-5 w-5" />
            <CardTitle>{t("languageSettings")}</CardTitle>
          </div>
          <CardDescription>{t("selectPreferredLanguage")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={preference}
            onValueChange={handleLanguageChange}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="auto" id="auto" />
              <Label htmlFor="auto">{t("useDeviceLanguage")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="en" id="en" />
              <Label htmlFor="en">English</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="de" id="de" />
              <Label htmlFor="de">Deutsch</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="es" id="es" />
              <Label htmlFor="es">Español</Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Store settings card */}
      {selectedStoreId && advancedInventoryTracking && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Settings className="h-5 w-5" />
              <div className="flex items-baseline space-x-2">
                <CardTitle>{t("storeSettings.title")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  ({t("currentStore")}:{" "}
                  {selectedStore?.name || t("noStoreSelected")})
                </span>
              </div>
            </div>
            <CardDescription>{t("configureStoreSettings")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label htmlFor="advanced-menu-tracking" className="text-base">
                  {t("advancedMenuTracking")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("advancedMenuTrackingDescription")}
                </p>
              </div>
              <Switch
                id="advanced-menu-tracking"
                checked={advancedMenuTracking}
                onCheckedChange={handleToggleRequest}
                disabled={isLoading}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Hours Settings */}
      {selectedStoreId && selectedStore && <BusinessHoursSettings />}

      {/* Add PaymentModeCard after StoreLocationSecurityCard */}
      {selectedStoreId && selectedStore && (
        <PaymentModeCard
          store={selectedStore}
          onUpdate={fetchStores}
          isLoading={isLoading}
        />
      )}

      <AdvancedTrackingDialog
        open={showTrackingDialog}
        onOpenChange={setShowTrackingDialog}
        onConfirm={handleToggleConfirm}
        isEnabling={pendingTrackingState === true}
      />
    </div>
  );
}
